import React, { useState, useEffect, useRef } from 'react';
import api from '../../../../services/apiConfig';
import useDebounce from '../../../../hooks/useDebounce';
import styles from './ProductSearchInput.module.css';

interface Product {
    ID: number;
    REFERENCIA: string;
    DESCRICAO: string;
    COD_PCP?: string;
    LINHA?: string;
    CUSTO?: number;
    MC?: number;
    PRECO_IMBISEG?: number | null;
}

interface ProductSearchInputProps {
    onSelect: (product: Product) => void;
    placeholder?: string;
    segmentoId?: number | null;
    initialValue?: string;
}

const ProductSearchInput: React.FC<ProductSearchInputProps> = ({ onSelect, placeholder = "Buscar produto...", segmentoId, initialValue = '' }) => {
    const [searchTerm, setSearchTerm] = useState(initialValue);
    const [results, setResults] = useState<Product[]>([]);
    const [loading, setLoading] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(-1);

    const debouncedSearchTerm = useDebounce(searchTerm, 500);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const listRef = useRef<HTMLDivElement>(null);
    const isFocusedRef = useRef(false);

    useEffect(() => {
        const searchProducts = async () => {
            if (debouncedSearchTerm.length < 2) {
                setResults([]);
                return;
            }

            setLoading(true);
            try {
                const response = await api.get('/products/search', {
                    params: { q: debouncedSearchTerm, segmentoId }
                });
                setResults(response.data);
                setSelectedIndex(-1);
                if (isFocusedRef.current) {
                    setIsOpen(true);
                }
            } catch (error) {
                console.error("Erro ao buscar produtos:", error);
            } finally {
                setLoading(false);
            }
        };

        searchProducts();
    }, [debouncedSearchTerm]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelect = (product: Product) => {
        onSelect(product);
        setSearchTerm(''); // Limpa o input após selecionar
        setIsOpen(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!isOpen) {
            if (e.key === 'ArrowDown' || e.key === 'Enter') {
                if (results.length > 0) setIsOpen(true);
            }
            return;
        }

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelectedIndex((prev) => {
                const nextIndex = prev < results.length - 1 ? prev + 1 : prev;
                scrollToIndex(nextIndex);
                return nextIndex;
            });
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelectedIndex((prev) => {
                const nextIndex = prev > 0 ? prev - 1 : prev;
                scrollToIndex(nextIndex);
                return nextIndex;
            });
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (selectedIndex >= 0 && selectedIndex < results.length) {
                handleSelect(results[selectedIndex]);
            }
        } else if (e.key === 'Escape') {
            setIsOpen(false);
        }
    };

    const scrollToIndex = (index: number) => {
        if (!listRef.current) return;
        const item = listRef.current.children[index] as HTMLElement;
        if (item) {
            item.scrollIntoView({ block: 'nearest' });
        }
    };

    return (
        <div className={styles.searchContainer} ref={wrapperRef}>
            <input
                type="text"
                className={styles.searchInput}
                placeholder={placeholder}
                value={searchTerm}
                onChange={(e) => {
                    setSearchTerm(e.target.value);
                    if (!isOpen && e.target.value.length >= 2) setIsOpen(true);
                }}
                onFocus={() => {
                    isFocusedRef.current = true;
                    if (searchTerm.length >= 2 && results.length > 0) {
                        setIsOpen(true);
                    }
                }}
                onBlur={() => {
                    isFocusedRef.current = false;
                }}
                onKeyDown={handleKeyDown}
            />

            {isOpen && results.length > 0 && (
                <div className={styles.dropdown} ref={listRef} style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    {results.map((product, index) => (
                        <div
                            key={product.ID}
                            className={styles.dropdownItem}
                            onMouseDown={(e) => {
                                e.preventDefault();
                                handleSelect(product);
                            }}
                            onClick={() => handleSelect(product)}
                            style={{ backgroundColor: index === selectedIndex ? '#dbeafe' : undefined }}
                        >
                            <span className={styles.productRef}>{product.REFERENCIA}</span>
                            <span className={styles.productDesc}>{product.DESCRICAO}</span>
                        </div>
                    ))}
                </div>
            )}

            {isOpen && !loading && results.length === 0 && debouncedSearchTerm.length >= 2 && (
                <div className={styles.dropdown}>
                    <div className={styles.dropdownItem} style={{ cursor: 'default', color: '#888' }}>
                        Nenhum produto encontrado.
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProductSearchInput;